import { isCommentInvoiceLine } from "./commentInvoiceLines";
import { JAB_ORG_ID } from "./jabJobDetails";

const enabled = value => value === true || String(value).toLowerCase() === "true";
const hasOwn = (value, field) => Object.prototype.hasOwnProperty.call(value, field);
const blank = value => value === null || (typeof value === "string" && value.trim() === "");

function catalogRecord(item, catalog) {
	// Manual/custom items have no dependable catalog classification.
	if (item.lineType === "custom" || item.recordType === "custom" || item.customPricing) return null;
	const hit = item.hit;
	if (hit && hasOwn(hit, "category") && hasOwn(hit, "labor") && hasOwn(hit, "labortime")) return hit;
	if (!catalog) return null;
	// Never match a renamed description or a fragment of an activity/pricing ID.
	const ids = [item.offerId, item.objectID, item.productId, item.id].filter(Boolean);
	for (const id of ids) {
		if (hasOwn(catalog, id)) return catalog[id];
	}
	return null;
}

export function isCustomerSuppliedTask(item = {}, orgId, catalog) {
	const record = orgId === JAB_ORG_ID ? catalogRecord(item, catalog) : null;
	return orgId === JAB_ORG_ID && (item.taskKind === "customerSupplied"
		|| [item, item.metadata, item.hit, item.hit && item.hit.metadata, record, record && record.metadata]
			.some(source => source && enabled(source.customerMaterials)));
}

export function getTaskGroup(item = {}, orgId, catalog) {
	if (orgId !== JAB_ORG_ID || isCommentInvoiceLine(item)) return "tasks";
	if (isCustomerSuppliedTask(item, orgId, catalog)) return "customerMaterials";
	if (Number(item.labortime) > 0) return "tasks";
	const record = catalogRecord(item, catalog);
	if (!record) return "tasks";
	const category = String(record.category || "");
	const materialCategory = category === "Material" || category.startsWith("Material/")
		|| category.startsWith("Custom Work and Casting/Casting Metals/");
	// Blank catalog labor fields mean no work component. Explicit zero is still
	// labor (e.g. Unbilled Labor); normalized invoice prices cannot tell us this.
	const noLabor = hasOwn(record, "labor") && hasOwn(record, "labortime")
		&& blank(record.labor) && blank(record.labortime);
	return materialCategory && noLabor ? "jabMaterials" : "tasks";
}

export function isMaterialOnlyTask(item, orgId, catalog) {
	return getTaskGroup(item, orgId, catalog) !== "tasks";
}

export function hasRecordedTaskTime(item = {}) {
	const minutes = Number(item.taskTime);
	return Number.isFinite(minutes) && minutes > 0;
}

export function canTrackTaskTime(item = {}, orgId, catalog) {
	if (!isMaterialOnlyTask(item, orgId, catalog) || hasRecordedTaskTime(item) || Number(item.labortime) > 0) return true;
	const record = catalogRecord(item, catalog);
	// Receipt grouping must not suppress work explicitly present in raw catalog
	// metadata. Zero-cost labor is still work; normalized invoice labor is not used.
	return Boolean(record && ["labor", "labortime"].some(field => hasOwn(record, field) && !blank(record[field])));
}

export function groupJobTaskIndexes(items, orgId, catalog) {
	if (orgId !== JAB_ORG_ID) return [{ key: "tasks", label: null, indexes: items.map((_item, index) => index) }];
	return [
		{ key: "tasks", label: "Tasks" },
		{ key: "jabMaterials", label: "Supplied Materials" },
		{ key: "customerMaterials", label: "Customer Supplied Materials" }
	].map(group => ({ ...group, indexes: items.reduce((indexes, item, index) => {
		if (item && getTaskGroup(item, orgId, catalog) === group.key) indexes.push(index);
		return indexes;
	}, []) })).filter(group => group.indexes.length > 0);
}

export function getTaskStatusLabel(item, orgId, catalog) {
	return item.checked ? (isCustomerSuppliedTask(item, orgId, catalog) ? "Received" : "Completed") : "Pending";
}

export function setTaskChecked(item, checked, identityId, orgId, catalog) {
	return {
		...item, checked,
		identityId: checked ? identityId : "",
		// Receipt/material status changes must not erase existing work history.
		...(!checked && !isMaterialOnlyTask(item, orgId, catalog) ? { taskTime: 0, taskTimeEnteredAt: "" } : {})
	};
}
